import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    inject,
    Injectable,
    makeStateKey,
    PLATFORM_ID,
    REQUEST,
    TransferState,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Logger } from '@shared/utils';
import { EnvironmentConfig } from './environment.interface';

/**
 * We use this unique key so we can access and infer the transfer state value (via SSR)
 * allowing us to encrypt the credentials so crawlers won't be able to scrape it easily
 * and also to prevent users from seeing the raw credentials in plain text when view-page sourced
 */
const OBFUSCATED_CONFIG_KEY = makeStateKey<string>('ntv360_manifest_payload');

@Injectable({
    providedIn: 'root',
})
export class EnvironmentService {
    private http = inject(HttpClient);
    private platformId = inject(PLATFORM_ID);
    private transferState = inject(TransferState);

    /**
     * Incoming SSR request, or `null` during build-time route extraction/prerender.
     * Used to skip the `/api/config` fetch when no live Express server is serving it.
     */
    private readonly request = inject(REQUEST, { optional: true });

    private _config: EnvironmentConfig | null = null;

    public get config(): EnvironmentConfig {
        if (!this._config) {
            throw new Error(
                '[EnvironmentService] Config not loaded. Ensure provideEnvironmentInitializer() is registered.',
            );
        }
        return this._config;
    }

    /**
     * Initializes the application's environment configuration.
     *
     * **Why we do this?**
     * We do this to prevent the data from getting crawled or scraped.
     * Also, when you `view-source` on the browser, it won't show the actual values but the encrypted text.
     *
     * @description
     * Orchestrates configuration loading across the two-pass Angular SSR lifecycle:
     * - **Server Pass (Node.js):** Bypasses the client hydration cache, fetches clear text
     * credentials from the backend `/api/config` route, and injects an obfuscated Base64
     * payload into the document state to block automated scraping.
     * - **Browser Pass (Hydration):** Intercepts initialization by extracting and decoding
     * the embedded state directly from the HTML source, completely bypassing redundant
     * network traffic.
     * * @returns An `Observable` emitting the finalized {@link EnvironmentConfig} required
     * to unblock application bootstrapping.
     */
    public load(): Observable<EnvironmentConfig> {
        if (this.transferState.hasKey(OBFUSCATED_CONFIG_KEY)) {
            const encryptedData = this.transferState.get(OBFUSCATED_CONFIG_KEY, '');

            try {
                this._config = JSON.parse(atob(encryptedData));
            } catch (e) {
                console.error('Failed to parse runtime environment profile', e);
            }

            this.transferState.remove(OBFUSCATED_CONFIG_KEY);
            return of(this._config!);
        }

        // Build-time route extraction / prerender bootstraps the app on the server with no
        // incoming request and no live Express server to serve `/api/config`. Issuing the
        // fetch there crashes route extraction (NG0201 from the server HTTP backend under
        // production optimization). Skip it — real SSR requests (REQUEST present) still load,
        // and the browser pass re-fetches when no TransferState payload was embedded.
        if (isPlatformServer(this.platformId) && !this.request) {
            return of(this._config as EnvironmentConfig);
        }

        return this.http
            .get<EnvironmentConfig>('/api/config', {
                headers: { initializer: 'true' },
                transferCache: false,
            })
            .pipe(
                tap((config) => {
                    this._config = config;
                    if (isPlatformServer(this.platformId) && config) {
                        const encryptedData = btoa(JSON.stringify(config));
                        this.transferState.set(OBFUSCATED_CONFIG_KEY, encryptedData);
                    }
                }),
                catchError((err: unknown) => {
                    // Never let a config-fetch failure crash bootstrap/route extraction.
                    // The browser pass will retry; consumers read `config` lazily.
                    Logger.error('EnvironmentService', 'Failed to load runtime config:', err);
                    return of(this._config as EnvironmentConfig);
                }),
            );
    }
}
